import React, { useEffect } from "react";
import { indexStore } from "../../../store/indexStore";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import EditSettingModel from "./EditSettingsModel";
import { Skeleton } from "src/Components/ui/skeleton";
import Visibility from "../../utils/Visibility";
import Iterator from "src/Components/utils/Iterator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Badge } from "../../ui/badge";
import {
  Settings as SettingsIcon,
  Truck,
  MapPin,
  DollarSign,
  Gift,
  Clock,
  Tag,
  Eye,
  Sparkles,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import UAEDirhamIcon from "../../utils/UAEDirhamIcon";

function Settings() {
  const { t } = useTranslation();
  const {
    settings,
    settingsLoading,
    fetchSettingsFunction,
    coupons,
    fetchCouponsFunction,
  } = indexStore();

  useEffect(() => {
    fetchSettingsFunction();
    if (coupons.length === 0) {
      fetchCouponsFunction();
    }
  }, [fetchSettingsFunction]);

  const formatValue = (value, key) => {
    if (typeof value === "boolean") {
      return value ? (
        <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {t("gloabl.enabled")}
        </Badge>
      ) : (
        <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          {t("gloabl.disabled")}
        </Badge>
      );
    }
    if (key === "first_register_coupon_id") {
      const coupon = coupons.find((coupon) => coupon.id === value);
      return coupon ? (
        <Badge variant="outline" className="gap-1">
          <Tag className="w-3 h-3" />
          {coupon.code}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }
    if (["free_delivery_min_total", "price_per_range", "bonus_ratio"].includes(key)) {
      return (
        <span className="flex items-center gap-1 font-semibold text-lg">
          <UAEDirhamIcon className="w-4 h-4" />
          {value ?? "—"}
        </span>
      );
    }
    if (["opening_time", "closing_time"].includes(key)) {
      return value ? (
        <span className="flex items-center gap-2 font-semibold">
          <Clock className="w-4 h-4 text-muted-foreground" />
          {value}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }
    return <span className="font-semibold text-lg">{value ?? "—"}</span>;
  };

  // Group settings by category
  const settingsCategories = [
    {
      title: t("gloabl.delivery_settings") || "Delivery Settings",
      description: t("gloabl.delivery_settings_desc") || "Configure delivery options and pricing",
      icon: Truck,
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-950/30",
      settings: [
        { key: "range_distance", label: t("gloabl.range_distance"), icon: MapPin },
        { key: "price_per_range", label: t("gloabl.price_per_range"), icon: DollarSign },
        { key: "free_delivery_min_total", label: t("gloabl.free_delivery_min_total"), icon: Truck },
      ],
    },
    {
      title: t("gloabl.points_rewards") || "Points & Rewards",
      description: t("gloabl.points_rewards_desc") || "Manage loyalty points and bonus system",
      icon: Gift,
      color: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-950/30",
      settings: [
        { key: "bonus_ratio", label: t("gloabl.bonus_ratio"), icon: Sparkles },
        { key: "max_points_usage_percentage", label: t("gloabl.max_points_usage_percentage"), icon: Gift },
      ],
    },
    {
      title: t("gloabl.general_settings") || "General Settings",
      description: t("gloabl.general_settings_desc") || "Basic application settings",
      icon: SettingsIcon,
      color: "text-gray-600",
      bgColor: "bg-gray-50 dark:bg-gray-950/30",
      settings: [
        { key: "hidden_status_order", label: t("gloabl.hidden_status_order"), icon: Eye },
        { key: "first_register_coupon_id", label: t("gloabl.first_register_coupon_id"), icon: Tag },
        { key: "opening_time", label: t("gloabl.opening_time"), icon: Clock },
        { key: "closing_time", label: t("gloabl.closing_time"), icon: Clock },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <SettingsIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">{t("dashbord.settings")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("gloabl.manage_settings") || "Manage and configure application settings"}
            </p>
          </div>
        </div>
        <EditSettingModel
          trigger={
            <Button size="lg" className="gap-2">
              <SettingsIcon className="w-4 h-4" />
              {t("gloabl.edit")} {t("gloabl.settings")}
            </Button>
          }
        />
      </div>

      {/* Settings Categories */}
      <Visibility visible={settingsLoading}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Iterator
            data={[1, 2, 3]}
            render={(e, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-48 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                </CardContent>
              </Card>
            )}
          />
        </div>
      </Visibility>

      <Visibility visible={!settingsLoading}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {settingsCategories.map((category, categoryIndex) => {
            const CategoryIcon = category.icon;
            return (
              <Card key={categoryIndex} className="hover:shadow-lg transition-shadow">
                <CardHeader className={`${category.bgColor} border-b`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${category.bgColor}`}>
                      <CategoryIcon className={`w-5 h-5 ${category.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{category.title}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {category.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {category.settings.map((setting, index) => {
                    const SettingIcon = setting.icon;
                    return (
                      <div
                        key={index}
                        className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <SettingIcon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground mb-1">
                                {setting.label}
                              </p>
                              <div className="text-base">
                                {formatValue(settings[setting.key], setting.key)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Visibility>
    </div>
  );
}

export default Settings;
